import { useState } from 'react';

/**
 * Render-time prev-value sync. Tracks the prior `value`; whenever it
 * changes, runs `onChange(prev, next)` synchronously during render. Use
 * for "external state changed → adjust local state" patterns where a
 * `useEffect` would be too late (visible flash) and the React docs
 * recommend the inline-setState approach.
 *
 * The handler may call setState — React schedules an immediate re-render
 * with the updated values, and on that render `value === prev` so the
 * branch terminates.
 *
 * See: https://react.dev/reference/react/useState#storing-information-from-previous-renders
 */
export function useSyncedState<T>(
  value: T,
  onChange: (prev: T, next: T) => void,
): void {
  const [prev, setPrev] = useState(value);
  if (value !== prev) {
    setPrev(value);
    onChange(prev, value);
  }
}
